/**
 * AdminSidebar — configures the shared @startus/ui Sidebar with the admin
 * panel's nav items (dashboard, open calls, startups, users, AI usage,
 * reference codes), a trailing arrow on every item, and a mobile-only
 * language switch + logout footer.
 */

import { useTranslation } from 'react-i18next';
import { Sidebar, LanguageSwitcher, type SidebarNavItem } from '@startus/ui';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

function DashboardIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.75 18.75H12.75M6.75 18.75V13.75M6.75 18.75H1.35C1.01863 18.75 0.75 18.4814 0.75 18.15V14.35C0.75 14.0186 1.01863 13.75 1.35 13.75H6.75M12.75 18.75V6.75M12.75 18.75H18.15C18.4814 18.75 18.75 18.4814 18.75 18.15V1.35C18.75 1.01863 18.4814 0.75 18.15 0.75H13.35C13.0186 0.75 12.75 1.01863 12.75 1.35V6.75M12.75 6.75H7.35C7.01863 6.75 6.75 7.01863 6.75 7.35V13.75"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function OpenCallsIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 14V6M14 14L20.1023 17.487C20.5023 17.7156 21 17.4268 21 16.9661V3.03391C21 2.57321 20.5023 2.28439 20.1023 2.51296L14 6M14 14H7C4.79086 14 3 12.2091 3 10C3 7.79086 4.79086 6 7 6H14"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M7.75716 19.3001L7 14H11L11.6772 18.7401C11.8476 19.9329 10.922 21 9.71716 21C8.73186 21 7.8965 20.2755 7.75716 19.3001Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function StartupsIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.58737 8.23597L11.1849 3.00376C11.5183 2.33208 12.4817 2.33208 12.8151 3.00376L15.4126 8.23597L21.2215 9.08017C21.9668 9.18848 22.2638 10.0994 21.7243 10.6219L17.5217 14.6918L18.5135 20.4414C18.6409 21.1798 17.8614 21.7428 17.1945 21.3941L12 18.678L6.80547 21.3941C6.1386 21.7428 5.35909 21.1798 5.48645 20.4414L6.47825 14.6918L2.27575 10.6219C1.73617 10.0994 2.03322 9.18848 2.77852 9.08017L8.58737 8.23597Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InvestorsIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 10h.01M15 10h.01"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AiUsageIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="7" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M9 2v5M15 2v5M9 17v5M15 17v5M2 9h5M2 15h5M17 9h5M17 15h5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function HealthIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 12h4l3-8 6 16 3-8h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ReferenceCodesIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M21 2H3C1.89543 2 1 2.89543 1 4V8C1 9.10457 1.89543 10 3 10H21C22.1046 10 23 9.10457 23 8V4C23 2.89543 22.1046 2 21 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1 14H23M1 18H23M5 6H5.01M9 6H15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LogoutIcon({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}

/**
 * Reusable sidebar component for the admin panel.
 * Displays navigation menu items with icons.
 * On mobile: slides in as a fixed overlay. On lg+: static in flex layout, collapsible.
 */
export default function AdminSidebar({ isOpen, onClose, onLogout }: AdminSidebarProps) {
  const { t } = useTranslation(['common', 'admin']);

  const items: SidebarNavItem[] = [
    {
      id: 'dashboard',
      label: t('admin:admin.sidebar.dashboard'),
      icon: <DashboardIcon className="w-5 h-5" />,
      path: '/admin/dashboard',
    },
    {
      id: 'open-calls',
      label: t('admin:admin.sidebar.opportunities'),
      icon: <OpenCallsIcon className="w-6 h-6" />,
      path: '/admin/open-calls',
    },
    {
      id: 'startups',
      label: t('admin:admin.sidebar.startups'),
      icon: <StartupsIcon className="w-6 h-6" />,
      path: '/admin/startups',
    },
    {
      id: 'users',
      label: t('admin:admin.sidebar.users'),
      icon: <UsersIcon className="w-6 h-6" />,
      path: '/admin/users',
    },
    {
      id: 'investors',
      label: t('admin:admin.sidebar.investors'),
      icon: <InvestorsIcon className="w-6 h-6" />,
      path: '/admin/investors',
    },
    {
      id: 'ai-usage',
      label: t('admin:admin.sidebar.aiUsage'),
      icon: <AiUsageIcon className="w-6 h-6" />,
      path: '/admin/ai-usage',
    },
    {
      id: 'health',
      label: t('admin:admin.sidebar.health'),
      icon: <HealthIcon className="w-6 h-6" />,
      path: '/admin/health',
    },
    {
      id: 'reference-codes',
      label: t('admin:admin.sidebar.referenceCodes'),
      icon: <ReferenceCodesIcon className="w-6 h-6" />,
      path: '/admin/reference-codes',
    },
  ];

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      items={items}
      storageKey="admin-sidebar-collapsed"
      showTrailingArrow
      collapseLabel={t('common:sidebar.collapse')}
      expandLabel={t('common:sidebar.expand')}
      closeLabel={t('common:sidebar.close')}
      mobileFooter={
        <>
          <div className="px-4 pb-2">
            <LanguageSwitcher variant="menuItem" align="start" />
          </div>
          <div className="border-t border-white/10 px-4 py-3">
            <button
              type="button"
              onClick={() => {
                onLogout();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
            >
              <LogoutIcon className="w-6 h-6 shrink-0" />
              <span className="font-medium">{t('auth.logout', { ns: 'common' })}</span>
            </button>
          </div>
        </>
      }
    />
  );
}
